// Google Tag Manager utility for tracking events
import TagManager from 'react-gtm-module';
import { calculateAccountAge } from './date-utils';

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;

let initialized = false;

/**
 * Initialize Google Tag Manager
 * Call this once when your app starts
 */
export const initGTM = () => {
  if (initialized || !GTM_ID) {
    console.warn('[GTM] Already initialized or GTM_ID not found');
    return;
  }

  TagManager.initialize({
    gtmId: GTM_ID,
  });

  initialized = true;
};

/**
 * Track a custom event by pushing to dataLayer
 * @param eventName - Name of the event (e.g., 'user_data_loaded')
 * @param eventData - Additional data for the event
 */
export const trackEvent = (
  eventName: string,
  eventData?: Record<string, any>
) => {
  if (!initialized) {
    console.warn('[GTM] Not initialized. Call initGTM() first.');
    return;
  }

  // Only send the event name and the data provided - no extra fields
  const finalData: Record<string, any> = {
    event: eventName,
    ...eventData,
  };

  // Log what we're sending to GTM

  // Push directly to window.dataLayer (not using TagManager.dataLayer wrapper)
  if (window.dataLayer) {
    window.dataLayer.push(finalData);
  } else {
    console.warn('[GTM] window.dataLayer not available');
  }
};

/**
 * Get membership tier from memberInfo
 */
export const getMembershipTierFromMemberInfo = (memberInfo: { subExpired?: boolean }): string => {
  return memberInfo.subExpired === false ? "subscriber" : "trial";
};

/**
 * Track user_data_loaded event with standard structure
 */
export const trackUserDataLoaded = (params: {
  memberInfo: {
    id: string;
    current_family?: string;
    family_member_type?: string;
    createdAt?: string;
    subExpired?: boolean;
  };
  membershipTier?: string;
  screenType: string;
  screenName: string;
  additionalPageData?: Record<string, any>;
  additionalUserProperties?: Record<string, any>;
  action?: string;
}) => {
  const { memberInfo, membershipTier, screenType, screenName, additionalPageData, additionalUserProperties, action } = params;

  // Use provided membershipTier or derive from memberInfo
  const tier = membershipTier || getMembershipTierFromMemberInfo(memberInfo);


  const pageData: Record<string, any> = {
    screen_type: screenType,
    screen_name: screenName,
    page_language: navigator.language || "en-US",
    environment: import.meta.env.VITE_ENVIRONMENT || "dev",
    ...additionalPageData,
  };

  // Add action if provided
  if (action) {
    pageData.action = action;
  }

  trackEvent("user_data_loaded", {
    user_properties: {
      user_id: memberInfo.id,
      family_id: memberInfo.current_family || "",
      login_status: "logged-in",
      user_profile: memberInfo.family_member_type === "fo" ? "family_owner" : "family_member",
      account_age: calculateAccountAge(memberInfo.createdAt),
      membership_tier: tier,
      ...additionalUserProperties,
    },
    page_data: pageData,
  });
};
